import { FloorplanPlacementGeometry } from '../types';
import { clamp01, toPercent } from './coordinates';

/** Where the details card sits relative to the placement it belongs to. */
export interface PlacementAnchor {
  /** Anchor point on the plan, as CSS percentages of the plan box. */
  left: string;
  top: string;
  /** Which of the card's edges the anchor point holds. */
  alignX: 'start' | 'center' | 'end';
  alignY: 'above' | 'below';
}

/**
 * Near an edge the card would hang off the plan if it stayed centred above the placement, so it
 * pivots on the edge that has room. These are the fractions of the plan at which it switches.
 */
const EDGE_X = 0.2;
const EDGE_Y = 0.3;

/**
 * Where to hang the details card for a given placement.
 *
 * Derived from the placement's own normalized geometry rather than from measured DOM: the card is
 * positioned inside the same percentage box the placements are drawn in, so no layout has to be read
 * and nothing needs recomputing while the plan is panned or zoomed.
 *
 * Above the placement by default, as in the prototype's `popStyle`. A placement near the top gets the
 * card below it instead, and one near a side gets it aligned to that side rather than centred —
 * otherwise the card lands outside the plan, where the viewport clips it.
 */
export const placementAnchor = ({ x, y, width, height }: FloorplanPlacementGeometry): PlacementAnchor => {
  const left = clamp01(x);
  const top = clamp01(y);
  const right = Math.min(1, left + clamp01(width));
  const bottom = Math.min(1, top + clamp01(height));
  const centerX = (left + right) / 2;

  const alignY = top < EDGE_Y ? 'below' : 'above';
  const alignX = centerX < EDGE_X ? 'start' : centerX > 1 - EDGE_X ? 'end' : 'center';

  return {
    // The card pivots on the placement's own edge when it is aligned to one, so it never covers the
    // placement the user just picked.
    // Rounded like the placement boxes are, and for the same reason — see `toPercent`.
    left: toPercent(alignX === 'start' ? left : alignX === 'end' ? right : centerX),
    top: toPercent(alignY === 'above' ? top : bottom),
    alignX,
    alignY,
  };
};
